import React from 'react';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface OrgNode {
  id: string;
  name: string;
  title: string;
  children: OrgNode[];
}

export function OrgChart({ 
  sponsorName, 
  sponsorTitle, 
  orgData 
}: { 
  sponsorName: string; 
  sponsorTitle: string, 
  orgData: any[] 
}) {
  if (!sponsorName) return null;

  const root: OrgNode = {
    id: "sponsor",
    name: sponsorName,
    title: sponsorTitle || "Executive Sponsor",
    children: []
  };

  const nodeMap = new Map<string, OrgNode>();
  nodeMap.set("sponsor", root);

  orgData.forEach(item => {
    if (item.name) {
      nodeMap.set(item.id, {
        id: item.id,
        name: item.name,
        title: item.title,
        children: []
      });
    }
  });

  orgData.forEach(item => {
    if (item.name && item.managerId) {
      const parent = nodeMap.get(item.managerId);
      const child = nodeMap.get(item.id);
      if (parent && child) {
        if (!parent.children.find(c => c.id === child.id) && parent.id !== child.id) { parent.children.push(child); }
      }
    }
  });

  return (
    <div className="w-full overflow-x-auto p-8 bg-gradient-to-b from-background to-muted/30 border rounded-xl flex justify-center items-center custom-scrollbar">
      <ul className="flex justify-center pl-0 m-0 w-max">
        <TreeNode node={root} isRoot visited={new Set()} />
      </ul>
    </div>
  );
}

function TreeNode({ node, isRoot = false, visited }: { node: OrgNode, isRoot?: boolean, visited: Set<string> }) {
  if (visited.has(node.id)) {
    return (
      <li className="relative float-left text-center flex flex-col items-center px-4 list-none text-red-500 text-xs">
        [Circular Reference: {node.name}]
      </li>
    );
  }
  
  const currentVisited = new Set(visited);
  currentVisited.add(node.id);

  return (
    <li className="relative float-left text-center flex flex-col items-center px-4 list-none">
      {!isRoot && (
        <div className="absolute top-0 left-1/2 w-px h-6 bg-border -translate-x-1/2 -mt-6"></div>
      )}
      
      {/* Node Box */}
      <div className="border bg-card shadow-sm rounded-lg p-4 w-48 text-center relative z-10 transition-all hover:border-primary/50 hover:shadow-md group">
        <div className="mx-auto bg-primary/10 group-hover:bg-primary/20 transition-colors w-10 h-10 rounded-full flex items-center justify-center mb-3">
          <User className="w-5 h-5 text-primary" />
        </div>
        <div className="font-semibold text-[15px] truncate" title={node.name}>{node.name}</div>
        <div className="text-sm text-muted-foreground truncate" title={node.title}>{node.title || "No Title"}</div>
      </div>

      {/* Children */}
      {node.children.length > 0 && (
        <div className="relative pt-6 mt-0 flex justify-center">
          {/* Vertical line down from parent */}
          <div className="absolute top-0 left-1/2 w-px h-6 bg-border -translate-x-1/2"></div>
          
          <ul className="flex justify-center pl-0 m-0 relative">
             {node.children.map((child, idx) => (
               <li key={child.id} className="relative text-center px-2 pt-6 pb-0 flex flex-col items-center list-none">
                 {/* Top horizontal connect line */}
                 {node.children.length > 1 && (
                   <div className={cn(
                     "absolute top-0 h-px bg-border",
                     idx === 0 ? "left-[50%] right-0" : 
                     idx === node.children.length - 1 ? "left-0 right-[50%]" : 
                     "left-0 right-0"
                   )} />
                 )}
                 <TreeNode node={child} visited={currentVisited} />
               </li>
             ))}
          </ul>
        </div>
      )}
    </li>
  );
}
